import { Component, Host, Inject, Input, OnDestroy, OnInit, Optional } from '@angular/core';
import { Action, Column, Parent } from '@miq/catalog';
import { ColumnErrorMessengerListenerService } from '@records/controls/common/listeners/column-error-messenger.service';
import { ControlListenerPlugin, ControlListenerPluginToken } from '@records/controls/models/control-listener-plugin';
import { BaseEdpPayload, DigestedActivityParams } from '@records/controls/services/update-record.model';
import { DataObject } from '@shared/models/data-object.model';
import { makeTemplatedRequest } from '@shared/utils/helpers/edp-request-builder';
import { ControlValidatorPlugin, ControlValidatorPluginToken } from '../../../models/control-validator-plugin';
import { FormControlMaker, FormControlMakerToken } from '../../../models/form-control-maker';
import { InputControlBuilder } from '@controls/core/input-control-builder.service';
import { BaseColumnControlValidatorPlugin } from '@controls/core/base-column-validator-plugin.service';
import { InputComponent } from '@controls/base/input/input.component';
import { HasEdpPayloadGenerator } from '@records/controls/models/payload-generator';
import { UntilDestroy } from '@ngneat/until-destroy';
import { AbstractControl } from '@angular/forms';
import { collapse } from '@shared/functions';

/**
 * Final and base component container for inputs, it injects basic functionality for build, listen and validate
 * It can be extended if most of the behaviors are shared and extend specific methods or behaviors that do not
 * matches with generic responsibilities. Example uom-container (it's a extension example but it's a base case as well)
 */
@UntilDestroy()
@Component({
  selector: 'miq-input-container-component',
  templateUrl: '../../../common/templates/input-container.component.html',
  providers: [
    // control builder
    { provide: FormControlMakerToken, useExisting: InputControlBuilder },
    // validator
    { provide: ControlValidatorPluginToken, useExisting: BaseColumnControlValidatorPlugin },
    // listeners
    {
      provide: ControlListenerPluginToken,
      useExisting: ColumnErrorMessengerListenerService,
      multi: true,
    },
  ],
  standalone: false,
})
export class InputContainerComponent
  extends InputComponent
  implements OnInit, OnDestroy, HasEdpPayloadGenerator<Column>
{
  @Input() showLabel = true;
  /**
   * Config to be used to generate payloads
   */
  edpConfig: Parent = null;

  constructor(
    // control builder
    @Inject(FormControlMakerToken) controlBuilder: FormControlMaker,
    // validator
    @Host() @Inject(ControlValidatorPluginToken) controlValidatorPlugin: ControlValidatorPlugin<Column>,
    // optional services
    // listeners
    @Host()
    @Optional()
    @Inject(ControlListenerPluginToken)
    listenerPlugins: Array<ControlListenerPlugin<Column>>
  ) {
    super(controlBuilder, controlValidatorPlugin, listenerPlugins);
  }

  ngOnInit(): void {
    super.ngOnInit();
    this.edpConfig = this.getEDPConfig(this.config);
  }

  /**
   * For all inputs the default action to use when updating is "update" attribute from Column.parent
   * Therefor using the config.parent object as the default edpConfig object for all inputs. (This
   * may not be the case for Section which usually just take the first column or selects an specific one)
   * @param config
   * @returns
   */
  getEDPConfig(config: Column): Parent {
    return config?.parent;
  }

  /**
   * Returns whether component is dirty or touched
   * @returns boolean
   */
  hasPendingPayloadToGenerate(): boolean {
    const _isControlTouchedAndDirty = (ctrl: AbstractControl) => ctrl.touched && ctrl.dirty;
    return (
      _isControlTouchedAndDirty(this.control) || Object.values(this.relatedControls).some(_isControlTouchedAndDirty)
    );
  }

  /**
   * Receives base payload object and merges to current form value (general or full object unless dataSelector$ is overridden)
   * on child classes. Most of edp request requires from entity type and id, therefore is provided by superclass implementation
   * @param basePayload
   * @returns
   */
  generatePayload(basePayload: BaseEdpPayload): DigestedActivityParams | DigestedActivityParams[] {
    return this.edpConfig.update != null
      ? this.doLogTransaction({ ...this.form.value, ...basePayload }, this.edpConfig.update)
      : null;
  }

  /**
   * This method is in generating the actual payload that needs to travel to edp by reading the "update" method from the received column
   * @param record
   * @param action
   * @returns
   */
  protected doLogTransaction(record: DataObject & BaseEdpPayload, action: Action): DigestedActivityParams {
    // uses action fields attribute & structure as the edp payload template
    // and populates it with record info
    const payload = makeTemplatedRequest(record, action.fields);

    // list of related column changes (list of Action)
    const otherActions = this._getRelatedColumnActionsForDirtyControls();
    const otherPayloads = collapse(otherActions.map(otherAction => makeTemplatedRequest(record, otherAction.fields)));

    // merge alpha and other changes
    return { data: Object.assign(payload, otherPayloads), method: action.method };
  }

  /**
   * list of Action for related columns which their control is dirty
   * @returns
   */
  protected _getRelatedColumnActionsForDirtyControls(): Action[] {
    return this.config
      .getRelatedColumns()
      .filter(this._isRelatedColumnControlDirtyAndUpdatable)
      .map(col => col.parent.update);
  }

  /**
   * Returns true if related control for given column is dirty AND given column has update action configured
   * @param col
   * @returns
   */
  private _isRelatedColumnControlDirtyAndUpdatable = (col: Column) => {
    const colControl = this.relatedControls[col.columnId];
    return colControl?.dirty && col.parent?.update != null;
  };

  /**
   * Uses Provided Logger to log te error
   * @param err
   */
  onPayloadGenerationError(err: Error): void {
    this.logger.error(err);
  }
}
